import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";

const rootKey = [API_ROUTES.PRODUCT_ANNOUNCEMENTS];

const invalidateAnnouncements = (queryClient, id) => {
  queryClient.invalidateQueries({ queryKey: rootKey });
  if (id) {
    queryClient.invalidateQueries({ queryKey: [...rootKey, id] });
  }
};

export const useProductAnnouncementsQuery = (params) =>
  useQuery({
    queryKey: [...rootKey, params],
    queryFn: async () => {
      const response = await api.get(API_ROUTES.PRODUCT_ANNOUNCEMENTS, {
        params,
      });
      return response.data.data;
    },
  });

export const useProductAnnouncementQuery = (id) =>
  useQuery({
    queryKey: [...rootKey, id],
    queryFn: async () => {
      const response = await api.get(
        `${API_ROUTES.PRODUCT_ANNOUNCEMENTS}/${id}`,
      );
      return response.data.data;
    },
    enabled: Boolean(id),
  });

export const useProductAnnouncementAudienceAccountsQuery = ({
  organizationId,
  accountModel,
  search,
  enabled,
}) =>
  useQuery({
    queryKey: [
      ...rootKey,
      "audienceAccounts",
      organizationId,
      accountModel,
      search,
    ],
    queryFn: async () => {
      const response = await api.get(
        `${API_ROUTES.PRODUCT_ANNOUNCEMENTS}/audienceAccounts`,
        { params: { organizationId, accountModel, search } },
      );
      return response.data.data;
    },
    enabled,
  });

export const useCreateProductAnnouncementMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ announcement, stagedMedia = [] }) => {
      if (!stagedMedia.length) {
        const response = await api.post(
          API_ROUTES.PRODUCT_ANNOUNCEMENTS,
          announcement,
        );
        return response.data.data;
      }
      const formData = new FormData();
      formData.append("announcement", JSON.stringify(announcement));
      formData.append(
        "media",
        JSON.stringify(
          stagedMedia.map(({ altText, caption }) => ({ altText, caption })),
        ),
      );
      stagedMedia.forEach(({ file }) => formData.append("files", file));
      const response = await api.post(
        API_ROUTES.PRODUCT_ANNOUNCEMENTS,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data.data;
    },
    onSuccess: (created) => {
      queryClient.setQueryData([...rootKey, created._id], created);
      invalidateAnnouncements(queryClient);
    },
  });
};

export const useUpdateProductAnnouncementMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const response = await api.put(
        `${API_ROUTES.PRODUCT_ANNOUNCEMENTS}/${id}`,
        payload,
      );
      return response.data.data;
    },
    onSuccess: (announcement) =>
      invalidateAnnouncements(queryClient, announcement._id),
  });
};

export const useDeleteProductAnnouncementMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      api.delete(`${API_ROUTES.PRODUCT_ANNOUNCEMENTS}/${id}`),
    onSuccess: () => invalidateAnnouncements(queryClient),
  });
};

export const useCopyProductAnnouncementMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await api.post(
        `${API_ROUTES.PRODUCT_ANNOUNCEMENTS}/${id}/copies`,
      );
      return response.data.data;
    },
    onSuccess: () => invalidateAnnouncements(queryClient),
  });
};

export const useUploadProductAnnouncementMediaMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file, altText, caption }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("altText", altText);
      formData.append("caption", caption);
      const response = await api.post(
        `${API_ROUTES.PRODUCT_ANNOUNCEMENTS}/${id}/media`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data.data;
    },
    onSuccess: (_, variables) =>
      invalidateAnnouncements(queryClient, variables.id),
  });
};

export const useDeleteProductAnnouncementMediaMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mediaId }) =>
      api.delete(
        `${API_ROUTES.PRODUCT_ANNOUNCEMENTS}/${id}/media/${mediaId}`,
      ),
    onSuccess: (_, variables) =>
      invalidateAnnouncements(queryClient, variables.id),
  });
};
